import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { TRUST_BADGES } from "@/lib/business";
import { usePublicSettings } from "@/lib/settings";
import { Phone, Mail, MapPin, ChevronDown, ShieldCheck, Zap, Truck, Clock, ArrowRight } from "lucide-react";

const FAQS = [
  {
    q: "How long do most repairs take?",
    a: "Most standard repairs are completed within 24–72 hours, depending on parts availability. We'll always keep you updated if there are any delays.",
  },
  {
    q: "Do you offer a warranty on your repairs?",
    a: "Yes — all labour is covered by a 21-day service warranty for the specific issue repaired. If the same fault returns within that window, we'll sort it at no extra charge.",
  },
  {
    q: "What if I'm not sure what's wrong with my scooter?",
    a: "No worries! We provide a free visual inspection and quote before any repair begins. If deeper diagnostics are needed (disassembly or testing), we'll confirm any fees with you first.",
  },
  {
    q: "Is a deposit required for repairs?",
    a: "Only for repairs that need special-order parts. Special-order parts (batteries, motors, controllers) may require a deposit of 50% of the total quote before ordering, with the balance due on completion. See Section 3 of our Terms & Conditions for how deposits are handled if a repair is cancelled.",
  },
  {
    q: "Do you offer mobile repair services?",
    a: "Yes, within the Horowhenua region! Travel fees may apply for jobs outside the area ($20–$80, confirmed beforehand). We'll come to you when it makes sense.",
  },
  {
    q: "What payment methods do you accept?",
    a: "Bank transfer is preferred — fast, free, and the details are on every invoice. Cash on collection is also fine, and approved customers can pay on invoice. See the full How-to-Pay guide at /how-to-pay for step-by-step instructions and tax-receipt info.",
  },
  {
    q: "Do you work on all scooter brands?",
    a: "We work on most E-Scooter, E-Bike, and Mobility / Power Chair brands including Xiaomi, Segway / Ninebot, Kaabo, Apollo, Pride Mobility, and more.",
  },
  {
    q: "What if new issues are discovered mid-repair?",
    a: "We'll always contact you before proceeding with any additional work. You'll receive an updated quote and nothing extra will be done without your approval.",
  },
];

const BADGE_ICONS = [ShieldCheck, Zap, Clock, Truck];

const FAQ = () => {
  const [open, setOpen] = useState(0);
  const settings = usePublicSettings();
  const tel = (settings.phone || "").replace(/[^+\d]/g, "");

  return (
    <div className="min-h-screen bg-spark-floor" data-testid="faq-page">
      <Navbar />

      <div className="bg-spark-teal-deep text-white border-b-4 border-spark-yellow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <p className="label-mono text-spark-yellow mb-2">Help & Information</p>
          <h1 className="font-display uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl font-bold leading-[0.95]">Frequently Asked Questions</h1>
          <p className="mt-4 text-slate-300 max-w-xl">Quick answers to the most common questions we get asked. Need something else? Just give us a yell.</p>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14 space-y-12">
        {/* Trust badges */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="trust-badges">
          {TRUST_BADGES.map((b, i) => {
            const Icon = BADGE_ICONS[i % BADGE_ICONS.length];
            return (
              <div key={b.title} className="surface-card p-5 flex items-start gap-3">
                <div className="size-10 rounded-sm bg-spark-teal-deep text-spark-yellow flex items-center justify-center shrink-0">
                  <Icon className="size-5" />
                </div>
                <div>
                  <p className="font-display uppercase tracking-tight font-bold text-spark-ink">{b.title}</p>
                  <p className="text-xs text-spark-slate">{b.subtitle}</p>
                </div>
              </div>
            );
          })}
        </section>

        {/* Quick contact strip */}
        <section className="surface-card p-6 grid sm:grid-cols-3 gap-4 text-sm">
          {settings.phone && (
            <a href={`tel:${tel}`} className="flex items-center gap-3 hover:text-spark-teal" data-testid="faq-phone">
              <Phone className="size-5 text-spark-teal" />
              <div>
                <p className="label-mono">Phone</p>
                <p>{settings.phone}</p>
              </div>
            </a>
          )}
          {settings.email && (
            <a href={`mailto:${settings.email}`} className="flex items-center gap-3 hover:text-spark-teal break-all" data-testid="faq-email">
              <Mail className="size-5 text-spark-teal shrink-0" />
              <div>
                <p className="label-mono">Email</p>
                <p className="font-semibold text-sm">{settings.email}</p>
              </div>
            </a>
          )}
          {settings.address_line && (
            <div className="flex items-center gap-3" data-testid="faq-address">
              <MapPin className="size-5 text-spark-teal" />
              <div>
                <p className="label-mono">Address</p>
                <p className="font-semibold">{settings.address_line}</p>
              </div>
            </div>
          )}
        </section>

        {/* FAQ list */}
        <section className="space-y-2" data-testid="faq-list">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <button
                key={i}
                onClick={() => setOpen(isOpen ? -1 : i)}
                className={`surface-card w-full text-left p-5 sm:p-6 transition-colors ${isOpen ? "border-spark-teal" : "hover:border-spark-ink"}`}
                data-testid={`faq-item-${i}`}
              >
                <div className="flex items-center justify-between gap-4">
                  <span className="font-display uppercase tracking-tight text-lg sm:text-xl font-bold text-spark-ink">{f.q}</span>
                  <ChevronDown className={`size-5 shrink-0 text-spark-teal transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </div>
                {isOpen && <p className="text-spark-slate text-sm leading-relaxed mt-3 pt-3 border-t border-spark-line">{f.a}</p>}
              </button>
            );
          })}
        </section>

        {/* Still have questions */}
        <section className="bg-spark-teal text-white rounded-md p-6 sm:p-10 grid md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-8">
            <p className="label-mono text-spark-yellow mb-2">Still have questions?</p>
            <h2 className="font-display uppercase tracking-tight text-3xl sm:text-4xl font-bold">We're happy to help — get in touch.</h2>
          </div>
          <div className="md:col-span-4 flex flex-wrap gap-3">
            <a href={`tel:${tel}`} className="btn-accent" data-testid="faq-call-btn"><Phone className="size-4" /> Call</a>
            <Link to="/contact" className="btn-outline-ink !border-white !text-white hover:!bg-white hover:!text-spark-teal" data-testid="faq-contact-btn">
              Contact <ArrowRight className="size-4" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FAQ;
